// MobileTopBar.tsx — Kopfzeile der Mobile-Kalenderansicht (`/termine`, SP6).
// Links Monat/Jahr (aus currentDate) + Heute/Blätter-Pfeile; rechts der
// View-Umschalter Tag/Woche/Monat (CalendarStateContext.setView) und — nur in
// der Tagesansicht — der Agenda-Sub-Modus-Toggle (Raster/Agenda, lokaler
// Shell-State). Zwei Icon-Buttons öffnen Filter- bzw. Kalender-Bottom-Sheet.
// Alle Touch-Ziele ≥44px. Agenda ist KEIN 4. View-Enum-Wert (Enum bleibt stabil
// für SP2–SP5), sondern ein Sub-Modus der Tagesansicht.

import React from "react";
import moment from "moment";
import "../../utils/momentDe"; // zentrale Locale-Quelle (de, Montag-Wochenstart, SP1)
import { REFINED } from "../risikoanalyseStep/tokens";
import { useCalendarState, type CalendarView } from "./CalendarStateContext";

export interface MobileTopBarProps {
    /** Sub-Modus der Tagesansicht. */
    dayMode: "grid" | "agenda";
    onDayModeChange: (m: "grid" | "agenda") => void;
    onOpenFilter: () => void;
    onOpenCalendars: () => void;
    /** Anzahl ausgeblendeter Filter (Badge am Filter-Button). */
    hiddenFilterCount?: number;
    /** Anzahl gewählter Kalender (Badge am Kalender-Button). */
    selectedCalendarCount?: number;
}

const VIEWS: { key: CalendarView; label: string }[] = [
    { key: "day", label: "Tag" },
    { key: "week", label: "Woche" },
    { key: "month", label: "Monat" },
];

export const MobileTopBar: React.FC<MobileTopBarProps> = ({
    dayMode,
    onDayModeChange,
    onOpenFilter,
    onOpenCalendars,
    hiddenFilterCount = 0,
    selectedCalendarCount = 0,
}) => {
    const { view, setView, currentDate, goPrev, goNext, goToToday } = useCalendarState();

    return (
        <div style={barStyle}>
            {/* Zeile 1: Monat/Jahr + Navigation + Icon-Aktionen. */}
            <div style={rowStyle}>
                <span style={monthStyle}>{moment(currentDate).format("MMMM YYYY")}</span>
                <div style={{ flex: 1 }} />
                <button type="button" aria-label="Vorheriger" onClick={goPrev} style={iconBtnStyle}>
                    ‹
                </button>
                <button type="button" onClick={goToToday} style={todayBtnStyle}>
                    Heute
                </button>
                <button type="button" aria-label="Nächster" onClick={goNext} style={iconBtnStyle}>
                    ›
                </button>
                <button
                    type="button"
                    aria-label="Filter"
                    title="Filter"
                    onClick={onOpenFilter}
                    style={iconBtnStyle}
                >
                    <FilterIcon />
                    {hiddenFilterCount > 0 && <span style={badgeStyle}>{hiddenFilterCount}</span>}
                </button>
                <button
                    type="button"
                    aria-label="Kalender"
                    title="Kalender"
                    onClick={onOpenCalendars}
                    style={iconBtnStyle}
                >
                    <CalendarIcon />
                    {selectedCalendarCount > 0 && (
                        <span style={badgeStyle}>{selectedCalendarCount}</span>
                    )}
                </button>
            </div>

            {/* Zeile 2: View-Umschalter + (nur Tag) Agenda-Sub-Modus. */}
            <div style={segRowStyle}>
                <div style={segGroupStyle} role="tablist" aria-label="Ansicht">
                    {VIEWS.map((v) => (
                        <button
                            key={v.key}
                            type="button"
                            role="tab"
                            aria-selected={view === v.key}
                            onClick={() => setView(v.key)}
                            style={segBtnStyle(view === v.key)}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>
                {view === "day" && (
                    <div style={segGroupStyle} role="tablist" aria-label="Tages-Modus">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={dayMode === "grid"}
                            onClick={() => onDayModeChange("grid")}
                            style={segBtnStyle(dayMode === "grid")}
                        >
                            Raster
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={dayMode === "agenda"}
                            onClick={() => onDayModeChange("agenda")}
                            style={segBtnStyle(dayMode === "agenda")}
                        >
                            Agenda
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const FilterIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden>
        <path
            d="M3 4h14l-5.5 7v5L8.5 18v-7L3 4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
    </svg>
);

const CalendarIcon: React.FC = () => (
    <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden>
        <rect
            x="3"
            y="4.5"
            width="14"
            height="12.5"
            rx="2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <path d="M3 8h14M7 3v3M13 3v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

const barStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "8px 10px",
    borderBottom: `1px solid ${REFINED.line}`,
    background: REFINED.white,
};

const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 4,
};

const monthStyle: React.CSSProperties = {
    font: `700 16px ${REFINED.fontUI}`,
    color: REFINED.ink,
    textTransform: "capitalize",
    whiteSpace: "nowrap",
};

const iconBtnStyle: React.CSSProperties = {
    position: "relative",
    width: 44,
    height: 44,
    minWidth: 44,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    background: REFINED.white,
    color: REFINED.ink2,
    cursor: "pointer",
    font: `600 18px ${REFINED.fontUI}`,
    padding: 0,
    flexShrink: 0,
};

const todayBtnStyle: React.CSSProperties = {
    height: 44,
    minWidth: 44,
    padding: "0 12px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    background: REFINED.white,
    color: REFINED.ink,
    cursor: "pointer",
    font: `600 14px ${REFINED.fontUI}`,
    flexShrink: 0,
};

const segRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
};

const segGroupStyle: React.CSSProperties = {
    display: "inline-flex",
    padding: 3,
    gap: 3,
    background: REFINED.fieldBg,
    border: `1px solid ${REFINED.line}`,
    borderRadius: 12,
};

const segBtnStyle = (active: boolean): React.CSSProperties => ({
    minHeight: 38,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    borderRadius: 9,
    cursor: "pointer",
    font: `${active ? 700 : 600} 14px ${REFINED.fontUI}`,
    background: active ? REFINED.aub : "transparent",
    color: active ? REFINED.white : REFINED.ink2,
});

const badgeStyle: React.CSSProperties = {
    position: "absolute",
    top: 2,
    right: 2,
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    borderRadius: 8,
    background: REFINED.aub,
    color: REFINED.white,
    font: `700 10px ${REFINED.fontUI}`,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};

export default MobileTopBar;
